import type { MemberTutorialKey, ResolvedBrand } from '@mlm/shared';
import type { SpotlightInteraction } from '@mlm/ui';
import { ACTIVATE_START_ROUTES, PAYMENT_ROUTES } from './decide';
import type { TutorialMode } from './store';

export type TutorialStep = {
  id: string;
  /** Pages this step belongs on (see `routeMatches`). */
  routes: string[];
  /** The `data-tour` value of the element to highlight; none centres the card. */
  target?: string;
  title: string;
  body: string;
  interaction: SpotlightInteraction;
  /** The button's label. Without one, the step waits for the member to act. */
  next?: string;
  /** Where the button goes. */
  go?: string;
  /** The tutorial's last step: reaching it records the outcome. */
  final?: boolean;
};

export type StepContext = {
  brand: Pick<ResolvedBrand, 'brandName' | 'kitLabel' | 'activationKitLabel'>;
  mode: TutorialMode;
  pendingOrderId: string | null;
  needsPayment: boolean;
  slotMayAutoCreate: boolean;
};

const TITLES: Record<MemberTutorialKey, (b: StepContext['brand']) => string> = {
  kit: (b) => `Buy your ${b.kitLabel}`,
  activate: () => 'Create your slot',
};

export const tutorialTitle = (key: MemberTutorialKey, brand: StepContext['brand']) =>
  TITLES[key](brand);

/**
 * LIST ORDER IS PRIORITY: `resolveStep` shows the first step that fits the page.
 * So anything that appears on top of a page (a dialog) comes before the steps
 * underneath it, and on one page the steps run top to bottom as Next is pressed.
 *
 * Every word goes through the tenant's labels, because a "kit" is a "course" on
 * some tenants.
 */
export function buildSteps(key: MemberTutorialKey, ctx: StepContext): TutorialStep[] {
  return key === 'kit' ? kitSteps(ctx) : activateSteps(ctx);
}

function kitSteps(ctx: StepContext): TutorialStep[] {
  const { brand, mode } = ctx;
  const practice = mode === 'practice';
  const kit = brand.kitLabel;
  const browse: SpotlightInteraction = practice ? 'none' : 'free';
  const whatsNext = ctx.slotMayAutoCreate
    ? 'Once your order is confirmed, your dashboard will show you what comes next.'
    : "Once your order is confirmed, your Code and PIN will appear in your Vault. We'll show you the next step then.";

  const steps: TutorialStep[] = [];
  const dashboard = ['/dashboard'];

  if (ctx.pendingOrderId && ctx.needsPayment && !practice) {
    steps.push({
      id: 'kit.resume',
      routes: dashboard,
      title: "Let's finish your order",
      body: `Your ${kit} order is waiting for payment. We'll show you how to pay for it.`,
      interaction: 'none',
      next: 'Continue',
      go: `/orders/${ctx.pendingOrderId}`,
    });
  } else {
    steps.push(
      practice
        ? {
            id: 'kit.welcome',
            routes: dashboard,
            title: `Practice: buying a ${kit}`,
            body: "We'll walk through the real screens. This is only practice, so nothing is ordered and you won't be charged.",
            interaction: 'none',
            next: 'Start practice',
          }
        : {
            id: 'kit.welcome',
            routes: dashboard,
            title: `Welcome to ${brand.brandName}!`,
            body: `A few quick steps will get you set up, starting with your ${brand.activationKitLabel}. You can skip this at any time.`,
            interaction: 'none',
            next: 'Show me',
          },
      {
        id: 'kit.pick',
        routes: dashboard,
        target: 'kit-buy-now',
        title: `Pick your ${brand.activationKitLabel}`,
        body: `Buying one gives you a Code and PIN, which you'll use to create your slot. Scroll to see them all, then tap Buy now on the one you want.`,
        // Any kit can be picked, not only the first one, whose button this marks.
        interaction: 'free',
      },
    );
  }

  const checkout = ['/checkout'];
  steps.push(
    {
      id: 'kit.placed-pay',
      routes: checkout,
      target: 'order-placed-pay',
      title: 'Your order is in',
      body: 'Tap the highlighted button to see how to pay for it.',
      interaction: 'target',
    },
    {
      id: 'kit.placed-done',
      routes: checkout,
      target: 'order-placed-done',
      title: "That's it for now",
      body: whatsNext,
      interaction: 'free',
      next: 'Done',
      final: true,
    },
    {
      id: 'kit.review',
      routes: checkout,
      target: 'checkout-confirm',
      title: 'One last check',
      body: 'Make sure everything is right, then tap this button to place your order.',
      interaction: 'free',
    },
    {
      id: 'kit.details',
      routes: checkout,
      target: 'checkout-details',
      title: 'Add your details',
      body: 'Your order needs a contact number and an address. Add or check them here.',
      interaction: browse,
      next: 'Next',
    },
    {
      id: 'kit.items',
      routes: checkout,
      target: 'checkout-items',
      title: 'Check your order',
      body: `Make sure this is the ${kit} you want, and choose how you'll get it.`,
      interaction: browse,
      next: 'Next',
    },
    {
      id: 'kit.method',
      routes: checkout,
      target: 'checkout-method',
      title: 'Choose how to pay',
      body: 'Pick a payment method. Some methods open a payment page, then bring you back here.',
      interaction: browse,
      next: 'Next',
    },
    practice
      ? {
          id: 'kit.place',
          routes: checkout,
          target: 'checkout-place',
          title: 'Place your order',
          body: "For a real purchase, this button creates your order. This is practice, so we'll stop here. Nothing was ordered.",
          interaction: 'none',
          next: 'Finish',
          final: true,
        }
      : {
          id: 'kit.place',
          routes: checkout,
          target: 'checkout-place',
          title: 'Place your order',
          body: "When everything looks right, tap this button. We'll show you how to pay next.",
          interaction: 'free',
        },
    {
      id: 'kit.finished',
      routes: PAYMENT_ROUTES,
      target: 'payment-finished',
      title: "That's it for now",
      body: whatsNext,
      interaction: 'free',
      next: 'Done',
      final: true,
    },
    {
      id: 'kit.pay-online',
      routes: PAYMENT_ROUTES,
      target: 'payment-online',
      title: 'Pay for your order',
      body: "Tap the pay button to open the payment page. You'll come back here after paying, and this page updates by itself.",
      interaction: 'free',
    },
    {
      id: 'kit.pay-upload',
      routes: PAYMENT_ROUTES,
      target: 'payment-upload',
      title: 'Pay, then send your proof',
      body: 'Send your payment using the How to pay details on this page. Then add the reference number, upload your receipt and tap Submit.',
      interaction: 'free',
    },
  );
  return steps;
}

function activateSteps(ctx: StepContext): TutorialStep[] {
  const { brand, mode } = ctx;
  const practice = mode === 'practice';
  const vault = ['/code-vault'];
  // In practice the only live controls on the page are the sample's own.
  const act: SpotlightInteraction = practice ? 'target' : 'free';

  return [
    practice
      ? {
          id: 'activate.welcome',
          routes: ACTIVATE_START_ROUTES,
          title: 'Practice: creating a slot',
          body: 'This uses a sample Code and PIN, so no slot is created.',
          interaction: 'none',
          next: 'Start practice',
          go: '/code-vault',
        }
      : {
          id: 'activate.welcome',
          routes: ACTIVATE_START_ROUTES,
          title: 'Your Code and PIN are ready',
          body: "Let's use them to create your slot. It only takes a minute, and you can skip this at any time.",
          interaction: 'none',
          next: 'Show me',
          go: '/code-vault',
        },
    practice
      ? {
          id: 'activate.done',
          routes: vault,
          target: 'redeem-done',
          title: "That's how it works",
          body: 'With a real code, this is where your new slot appears. This was only practice, so nothing was created.',
          interaction: 'free',
          next: 'Finish',
          final: true,
        }
      : {
          id: 'activate.done',
          routes: vault,
          target: 'redeem-done',
          title: 'Your slot is active',
          body: "Nice work, you're all set. Your dashboard now shows your slot and everything that comes with it.",
          interaction: 'free',
          next: 'Done',
          final: true,
        },
    {
      id: 'activate.confirm',
      routes: vault,
      target: 'redeem-confirm',
      title: 'Check and confirm',
      body: "This shows who you'll be placed under. Activation can't be changed later, so check it first, then tap Confirm & activate.",
      interaction: act,
    },
    {
      id: 'activate.form',
      routes: vault,
      target: 'redeem-form',
      title: 'Check your details',
      body: "Your PIN is already filled in. If an affiliate code is shown, check that it's right, then tap Continue.",
      interaction: act,
    },
    {
      id: 'activate.row',
      routes: vault,
      target: 'vault-code-row',
      title: 'This is your Code and PIN',
      body: `Each ${brand.kitLabel} you buy gives you one Code and PIN. They're what creates your slot.`,
      interaction: 'none',
      next: 'Next',
    },
    {
      id: 'activate.redeem',
      routes: vault,
      target: 'vault-redeem',
      title: 'Tap Redeem',
      body: "This starts creating your slot. You'll get to check everything before it's final.",
      interaction: 'target',
    },
  ];
}
